import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftRight,
  CreditCard,
  EllipsisVertical,
  Power,
} from 'lucide-react'
import React, { useContext, useState } from 'react'
import { toast } from 'sonner'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { Banner, Button, DropdownMenu, IconButton } from 'slab'
import {
  getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey,
  getGetBrokersUnprocessedEnrollmentsIdQueryKey,
  useGetBrokersMembersMemberIdStripeIssuingCards,
  usePostBrokersMembersMemberIdStripeIssuingCards,
  usePostBrokersMembersMemberIdStripeIssuingCardsCardIdActivate,
} from 'utils/web_api'
import {
  type BrokersEnrollmentEnriched,
  type BrokersPaginatedStripeIssuingCardsList,
  type BrokersStripeIssuingCard,
} from 'utils/web_api.schemas'

import EnrollmentPaymentMethodRemapSheet from './EnrollmentPaymentMethodRemapSheet'
import StripeCardDetailsModal from './StripeCardDetailsModal'

interface EnrollmentPaymentMethodProps {
  memberId: string
  enrollment: BrokersEnrollmentEnriched
}

const PaymentMethodDropdownMenu = ({
  memberId,
  cardId,
  isActive = false,
  selectedEnrollment,
}: {
  memberId: string
  cardId?: string
  isActive?: boolean
  selectedEnrollment: BrokersEnrollmentEnriched
}) => {
  const queryClient = useQueryClient()
  const [isRemapSheetOpen, setIsRemapSheetOpen] = useState(false)

  const activateCardMutation =
    usePostBrokersMembersMemberIdStripeIssuingCardsCardIdActivate({
      mutation: {
        onSuccess: (updatedCard: BrokersStripeIssuingCard) => {
          toast.success('Card activated successfully', {
            position: 'top-center',
          })

          // Update the cards cache with the reactivated card
          const cardsQueryKey =
            getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey(
              memberId,
              undefined
            )
          queryClient.setQueryData<BrokersPaginatedStripeIssuingCardsList>(
            cardsQueryKey,
            (oldData) => {
              if (!oldData) return oldData

              return {
                ...oldData,
                data:
                  oldData.data?.map((card) =>
                    card.id === updatedCard.id ? updatedCard : card
                  ) || [],
              }
            }
          )
        },
        onError: (error: any) => {
          toast.error(
            `Failed to activate card: ${error?.message || 'Unknown error'}`,
            {
              position: 'top-center',
            }
          )
        },
      },
    })

  const handleReactivateCard = (cardId: string) => {
    activateCardMutation.mutate({
      memberId,
      cardId,
    })
  }

  return (
    <>
      <DropdownMenu
        align='end'
        trigger={
          <IconButton
            label='Card settings'
            variant='secondary'
            icon={EllipsisVertical}
          />
        }
      >
        <DropdownMenu.Item
          leftIcon={ArrowLeftRight}
          onSelect={() => setIsRemapSheetOpen(true)}
        >
          Remap payment method
        </DropdownMenu.Item>

        {!isActive && cardId && (
          <DropdownMenu.Item
            leftIcon={Power}
            onSelect={() => handleReactivateCard(cardId)}
          >
            Reactivate card
          </DropdownMenu.Item>
        )}
      </DropdownMenu>

      <EnrollmentPaymentMethodRemapSheet
        memberId={memberId}
        selectedEnrollment={selectedEnrollment}
        isOpen={isRemapSheetOpen}
        onOpenChange={setIsRemapSheetOpen}
      />
    </>
  )
}

const EnrollmentPaymentMethod: React.FC<EnrollmentPaymentMethodProps> = ({
  memberId,
  enrollment,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { isUserThatchAdmin, stripePublishableKey } = useContext(BrokersMemberContext)

  const { data: stripeCards, isLoading, error } = useGetBrokersMembersMemberIdStripeIssuingCards(
    memberId,
    undefined,
    {
      query: {
        enabled: !!memberId,
      },
    }
  )

  const createCardMutation = usePostBrokersMembersMemberIdStripeIssuingCards({
    mutation: {
      onSuccess: (newCard) => {
        toast.success('Card successfully created', {
          position: 'top-center',
        })

        // Update the cards cache with the new card
        const cardsQueryKey =
          getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey(
            memberId,
            undefined
          )
        queryClient.setQueryData<BrokersPaginatedStripeIssuingCardsList>(
          cardsQueryKey,
          (oldData) => {
            if (!oldData) {
              return {
                data: [newCard],
                pagination: undefined,
              }
            }

            return {
              ...oldData,
              data: [newCard, ...(oldData.data || [])],
            }
          }
        )

        // Invalidate enrollments query since the enrollment's payment method fields will be updated
        queryClient.invalidateQueries({
          queryKey: getGetBrokersUnprocessedEnrollmentsIdQueryKey(
            enrollment.id
          ),
        })
      },
      onError: (error: GrapeAPIErrorType) => {
        toast.error(
          `Failed to create card: ${error?.errors?.[0] || 'Unknown error'}`,
          {
            position: 'top-center',
          }
        )
      },
    },
  })

  const handleShowCard = (cardId: string) => {
    if (!stripePublishableKey) {
      toast.error('Stripe configuration is missing. Please contact support.', {
        position: 'top-center',
      })
      return
    }
    setSelectedCardId(cardId)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedCardId(null)
  }

  const handleCreateCard = () => {
    createCardMutation.mutate({
      memberId,
      data: {
        enrollment_id: enrollment.id,
      },
    })
  }

  if (error) {
    const errorMessage = (error as GrapeAPIErrorType)?.errors?.[0] || 'Unknown error'
    return (
      <div className='mb-6'>
        <Banner type='critical' title={`Error fetching card details: ${errorMessage}`} />
      </div>
    )
  }

  const renderLoadingSkeleton = () => {
    return (
      <div className='mb-6'>
        <div className='text-sm text-gray-600 mb-2'>
          <div className='h-4 bg-gray-200 rounded w-32 animate-pulse' />
        </div>
        <div className='flex items-center gap-2'>
          <div className='h-10 bg-gray-200 rounded w-full animate-pulse' />
          {isUserThatchAdmin && (
            <div className='h-10 w-10 bg-gray-200 rounded animate-pulse' />
          )}
        </div>
      </div>
    )
  }

  const StripeCardDisplay = ({
    stripeCard,
  }: {
    stripeCard: BrokersStripeIssuingCard
  }) => {
    // The below logic assumes if a card is not active or inactive, it is canceled.
    const isActive = stripeCard.remote_status === 'active'
    const isInactive = stripeCard.remote_status === 'inactive'

    const statusText = isActive
      ? 'Stripe Card'
      : isInactive
        ? 'Stripe Card (Inactive)'
        : 'Stripe Card (Canceled)'

    const buttonText = isActive
      ? `Show ••••${stripeCard.last4}`
      : isInactive
        ? `Inactive ••••${stripeCard.last4}`
        : `Canceled ••••${stripeCard.last4}`

    return (
      <div className='mb-6'>
        <div className='text-sm text-gray-600 mb-2'>
          <span className='font-semibold'>Payment Method:</span> {statusText}
        </div>
        <div className='flex items-center gap-2'>
          <Button
            variant='secondary'
            width='full'
            onClick={isActive ? () => handleShowCard(stripeCard.id) : undefined}
            id={isActive ? `show-card-${enrollment.id}` : undefined}
            disabled={!isActive}
            leftIcon={CreditCard}
          >
            {isActive ? buttonText : <span className='text-sm'>{buttonText}</span>}
          </Button>
          {isUserThatchAdmin && (
            <PaymentMethodDropdownMenu
              memberId={memberId}
              cardId={stripeCard.id}
              isActive={isActive}
              selectedEnrollment={enrollment}
            />
          )}
        </div>
      </div>
    )
  }

  const renderPaymentMethod = () => {
    // Show loading skeleton while fetching cards
    if (isLoading) {
      return renderLoadingSkeleton()
    }

    // Reimbursement only.
    if (enrollment.reimbursement_only) {
      return (
        <div className='mb-6'>
          <div className='text-sm text-gray-600 mb-2'>
            <span className='font-semibold'>Payment Method:</span> Reimbursement
            Only: Member Pays
          </div>
        </div>
      )
    }

    // No card configured yet.
    if (!enrollment.payment_method_id || !enrollment.payment_method_type) {
      return (
        <div className='mb-6'>
          <div className='text-sm text-gray-600 mb-2'>
            <span className='font-semibold'>Payment Method:</span> Stripe Card
          </div>
          <div className='flex items-center gap-2'>
            <Button
              variant='primary'
              width='full'
              onClick={handleCreateCard}
              id={`create-card-${enrollment.id}`}
              disabled={createCardMutation.isPending}
            >
              {createCardMutation.isPending ? 'Creating...' : 'Create card'}
            </Button>
            {isUserThatchAdmin && (
              <PaymentMethodDropdownMenu
                memberId={memberId}
                selectedEnrollment={enrollment}
              />
            )}
          </div>
        </div>
      )
    }

    // Stripe issuing card
    if (enrollment.payment_method_type === 'Stripe Card' && stripeCards?.data) {
      const stripeCard = stripeCards.data.find(
        (card) => card.id === enrollment.payment_method_id
      )

      if (stripeCard) {
        return <StripeCardDisplay stripeCard={stripeCard} />
      }
    }

    // Other payment method types
    return (
      <div className='mb-6'>
        <div className='text-sm text-gray-600 mb-2'>
          <span className='font-semibold'>Payment Method:</span>{' '}
          {enrollment.payment_method_type}
        </div>
      </div>
    )
  }

  return (
    <div>
      {renderPaymentMethod()}

      {/* Modal for showing card details */}
      {isModalOpen && selectedCardId && stripePublishableKey && (
        <StripeCardDetailsModal
          memberId={memberId}
          cardId={selectedCardId}
          enrollmentId={enrollment.id}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          stripePublishableKey={stripePublishableKey!}
        />
      )}
    </div>
  )
}

export default EnrollmentPaymentMethod
